import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Megaphone, Loader2, CheckCircle, AlertCircle, Upload, X, Image, Video } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const SPONSOR_VARIATION_ID = 'FY264MQD7XKYCPZSWYG4JILI';

const SponsorPage = () => {
  const [step, setStep] = useState('form');
  const [sponsorName, setSponsorName] = useState('');
  const [headline, setHeadline] = useState('');
  const [description, setDescription] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [contactInfo, setContactInfo] = useState('');
  const [displayContactPublicly, setDisplayContactPublicly] = useState(false);
  const [requestedDate, setRequestedDate] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [error, setError] = useState('');
  const [processingMsg, setProcessingMsg] = useState('');
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'complete') {
      window.history.replaceState({}, '', window.location.pathname);
      const pendingId = sessionStorage.getItem('med_pending_sponsor_id');
      const orderId = sessionStorage.getItem('med_pending_sponsor_order');
      if (pendingId && orderId) {
        setStep('confirming');
        startPolling(pendingId, orderId);
      }
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const startPolling = (pendingId, orderId) => {
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`${API}/api/sponsor/pending/${pendingId}/status?order_id=${orderId}`);
        const data = await res.json();
        if (data.status && data.status !== 'pending_payment') {
          clearInterval(pollRef.current);
          sessionStorage.removeItem('med_pending_sponsor_id');
          sessionStorage.removeItem('med_pending_sponsor_order');
          if (data.status === 'expired') {
            setError('Submission expired. Please try again.');
            setStep('error');
          } else {
            setStep('submitted');
          }
          return;
        }
      } catch (e) { /* retry */ }
      if (attempts >= 60) {
        clearInterval(pollRef.current);
        setError('Payment verification timed out. If you were charged, your submission will be processed automatically.');
        setStep('error');
      }
    }, 2000);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (!isImage && !isVideo) {
      setError('Please select an image or video file.');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      setError('File too large (max 100MB).');
      return;
    }
    setMediaFile(file);
    setMediaType(isImage ? 'image' : 'video');
    setMediaPreview(URL.createObjectURL(file));
    setError('');
  };

  const clearMedia = () => {
    if (mediaPreview) URL.revokeObjectURL(mediaPreview);
    setMediaFile(null);
    setMediaPreview(null);
    setMediaType(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePayAndSubmit = async () => {
    if (!sponsorName.trim()) { setError('Sponsor / Business name is required.'); return; }
    if (!description.trim()) { setError('Description is required.'); return; }
    setError('');
    setStep('processing');
    try {
      setProcessingMsg('Saving your submission...');
      const pendingRes = await fetch(`${API}/api/sponsor/pending`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sponsor_name: sponsorName.trim(),
          headline: headline.trim(),
          description: description.trim(),
          website_url: websiteUrl.trim(),
          cta_text: ctaText.trim(),
          contact_info: contactInfo.trim(),
          requested_date: requestedDate,
          display_contact_publicly: displayContactPublicly,
        }),
      });
      if (!pendingRes.ok) {
        const err = await pendingRes.json();
        throw new Error(err.detail || 'Failed to save submission');
      }
      const { pending_id } = await pendingRes.json();

      if (mediaFile) {
        setProcessingMsg('Uploading media...');
        const formData = new FormData();
        formData.append('file', mediaFile);
        const uploadRes = await fetch(`${API}/api/sponsor/pending/${pending_id}/upload`, {
          method: 'POST',
          body: formData,
        });
        if (!uploadRes.ok) {
          const err = await uploadRes.json();
          throw new Error(err.detail || 'Failed to upload media');
        }
      }

      setProcessingMsg('Redirecting to payment...');
      const checkoutRes = await fetch(`${API}/api/store/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variation_id: SPONSOR_VARIATION_ID,
          quantity: 1,
          entitlement_type: 'sponsor_room',
          redirect_path: '/sponsor',
          pending_submission_id: pending_id,
        }),
      });
      if (!checkoutRes.ok) throw new Error('Failed to create checkout');
      const { url, order_id } = await checkoutRes.json();

      sessionStorage.setItem('med_pending_sponsor_id', pending_id);
      sessionStorage.setItem('med_pending_sponsor_order', order_id);
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setStep('form');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0a2e] to-[#0d1b2a] flex items-center justify-center p-4" data-testid="sponsor-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-600/20 border border-amber-500/30 mb-4">
            <Megaphone className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Sponsor the Room</h1>
          <p className="text-white/60 text-sm mt-1">$150 — Your brand featured for a full day</p>
        </div>

        {step === 'form' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white/5 backdrop-blur-lg rounded-xl border border-white/10 p-6 space-y-4" data-testid="sponsor-form">
            <div>
              <label className="block text-amber-200 text-sm mb-1">Sponsor / Business Name *</label>
              <input
                type="text" value={sponsorName} onChange={e => setSponsorName(e.target.value)}
                placeholder="Your business name"
                className="w-full bg-black/20 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-amber-500 focus:outline-none"
                data-testid="input-sponsor-name"
              />
            </div>
            <div>
              <label className="block text-amber-200 text-sm mb-1">Description *</label>
              <textarea
                value={description} onChange={e => setDescription(e.target.value)}
                placeholder="Tell us about your business or what you'd like to promote" rows={3}
                className="w-full bg-black/20 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-amber-500 focus:outline-none resize-none"
                data-testid="input-description"
              />
            </div>
            <div>
              <label className="block text-amber-200 text-sm mb-1">Headline</label>
              <input
                type="text" value={headline} onChange={e => setHeadline(e.target.value)}
                placeholder="Short headline for your ad"
                className="w-full bg-black/20 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-amber-500 focus:outline-none"
                data-testid="input-headline"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-amber-200 text-sm mb-1">Website / URL</label>
                <input
                  type="url" value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full bg-black/20 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-amber-500 focus:outline-none"
                  data-testid="input-website-url"
                />
              </div>
              <div>
                <label className="block text-amber-200 text-sm mb-1">CTA Text</label>
                <input
                  type="text" value={ctaText} onChange={e => setCtaText(e.target.value)}
                  placeholder="e.g. Visit Us"
                  className="w-full bg-black/20 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-amber-500 focus:outline-none"
                  data-testid="input-cta"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-amber-200 text-sm mb-1">Contact Info</label>
                <input
                  type="text" value={contactInfo} onChange={e => setContactInfo(e.target.value)}
                  placeholder="Email or phone"
                  className="w-full bg-black/20 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-amber-500 focus:outline-none"
                  data-testid="input-contact"
                />
                {contactInfo.trim() && (
                  <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="contact-public-toggle">
                    <input
                      type="checkbox" checked={displayContactPublicly}
                      onChange={e => setDisplayContactPublicly(e.target.checked)}
                      className="w-3.5 h-3.5 rounded border-white/20 bg-black/20 accent-amber-500"
                    />
                    <span className="text-white/40 text-[11px]">Display this contact info publicly</span>
                  </label>
                )}
              </div>
              <div>
                <label className="block text-amber-200 text-sm mb-1">Requested Date</label>
                <input
                  type="date" value={requestedDate} onChange={e => setRequestedDate(e.target.value)}
                  className="w-full bg-black/20 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-amber-500 focus:outline-none [color-scheme:dark]"
                  data-testid="input-requested-date"
                />
              </div>
            </div>

            {/* Media Upload */}
            <div>
              <label className="block text-amber-200 text-sm mb-2">Sponsorship Media (Image or Video)</label>
              {!mediaPreview ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center cursor-pointer hover:border-amber-500/50 transition-colors"
                  data-testid="media-upload-area"
                >
                  <Upload className="w-8 h-8 text-white/40 mx-auto mb-2" />
                  <p className="text-white/40 text-sm">Click to upload image or video</p>
                  <p className="text-white/20 text-xs mt-1">JPG, PNG, WebP, MP4, WebM — Max 100MB</p>
                </div>
              ) : (
                <div className="relative rounded-lg overflow-hidden border border-white/20" data-testid="media-preview">
                  {mediaType === 'image' ? (
                    <img src={mediaPreview} alt="Preview" className="w-full max-h-64 object-contain bg-black/40" />
                  ) : (
                    <video src={mediaPreview} controls className="w-full max-h-64 bg-black/40" />
                  )}
                  <button
                    onClick={clearMedia} type="button"
                    className="absolute top-2 right-2 bg-black/60 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
                    data-testid="clear-media-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                    {mediaType === 'image' ? <Image className="w-3 h-3" /> : <Video className="w-3 h-3" />}
                    {mediaType}
                  </div>
                </div>
              )}
              <input
                ref={fileInputRef} type="file"
                accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime"
                onChange={handleFileSelect} className="hidden"
                data-testid="media-file-input"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 shrink-0" /> <span>{error}</span>
              </div>
            )}
            <button
              onClick={handlePayAndSubmit}
              disabled={!sponsorName.trim() || !description.trim()}
              className="w-full bg-gradient-to-r from-amber-600 to-amber-800 hover:from-amber-500 hover:to-amber-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-6 py-4 transition-all"
              data-testid="pay-and-sponsor-btn"
            >
              Pay $150 & Submit Sponsorship
            </button>
            <p className="text-white/30 text-xs text-center">Secure payment via Square. Your sponsorship will be reviewed before publishing.</p>
          </motion.div>
        )}

        {step === 'processing' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white/5 backdrop-blur-lg rounded-xl border border-white/10 p-8 text-center" data-testid="sponsor-processing">
            <Loader2 className="w-10 h-10 text-amber-400 animate-spin mx-auto mb-4" />
            <p className="text-white font-medium">{processingMsg || 'Preparing your sponsorship...'}</p>
          </motion.div>
        )}

        {step === 'confirming' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white/5 backdrop-blur-lg rounded-xl border border-white/10 p-8 text-center" data-testid="sponsor-confirming">
            <Loader2 className="w-10 h-10 text-amber-400 animate-spin mx-auto mb-4" />
            <p className="text-white font-medium">Verifying payment...</p>
            <p className="text-white/40 text-sm mt-1">Your sponsorship will be submitted automatically</p>
          </motion.div>
        )}

        {step === 'submitted' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-lg rounded-xl border border-green-400/30 p-8 text-center"
            data-testid="sponsor-submitted"
          >
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Sponsorship Submitted!</h2>
            <p className="text-white/60 text-sm">Your sponsorship is now being reviewed by our team. We'll publish it on your requested date.</p>
            <a href="/" className="inline-block mt-6 text-amber-400 hover:text-amber-300 text-sm font-medium" data-testid="back-home-link">
              ← Back to Mediterranean Club
            </a>
          </motion.div>
        )}

        {step === 'error' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-red-500/10 backdrop-blur-lg rounded-xl border border-red-400/30 p-8 text-center" data-testid="sponsor-error">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-white mb-2">Something went wrong</h2>
            <p className="text-white/60 text-sm">{error}</p>
            <button onClick={() => { setStep('form'); setError(''); }} className="mt-4 text-amber-400 hover:text-amber-300 text-sm font-medium">
              Try again
            </button>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default SponsorPage;
